import React, { useState } from 'react';

/**
 * AI Qualification Agent Modal
 * Appears after profile creation to categorize workers
 * and guide them to personalized resources
 */
function ProfileQualificationAgent({ user, onComplete, onClose }) {
  const [step, setStep] = useState('welcome'); // welcome | visa | disability | work-style | complete
  const [selections, setSelections] = useState({
    workerType: null,  // 'migrant' | 'disabled' | 'standard'
    visaStatus: null,  // for migrants
    disabilityType: null, // for disabled workers
    workStyle: null, // preferences
  });

  const handleSelect = (key, value) => {
    setSelections(prev => ({ ...prev, [key]: value }));
  };

  const saveQualifications = async () => {
    try {
      await fetch('/api/account/qualifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(selections)
      });
      localStorage.setItem('sm_profile_qualified', 'true');
      localStorage.setItem('sm_worker_qualifications', JSON.stringify(selections));
      onComplete(selections);
    } catch (err) {
      console.error('Could not save qualifications:', err);
      onComplete(selections);
    }
  };

  // ─── WELCOME SCREEN ───────────────────────────────
  if (step === 'welcome') {
    return (
      <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
          <div className="text-4xl mb-4">🤖</div>
          <h2 className="font-brand text-2xl font-bold text-neutral-900 mb-2">Meet your AI guide</h2>
          <p className="text-sm text-neutral-600 mb-6">
            We'll help you find the right shifts and resources in just 60 seconds. This makes your experience way better.
          </p>
          <div className="space-y-2 mb-6">
            <div className="flex gap-3">
              <span className="text-xl">🌏</span>
              <div>
                <p className="text-sm font-semibold text-neutral-900">Are you a migrant worker?</p>
                <p className="text-xs text-neutral-500">We'll connect you to visa tracking, housing, banking</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-xl">♿</span>
              <div>
                <p className="text-sm font-semibold text-neutral-900">Do you have a disability?</p>
                <p className="text-xs text-neutral-500">We'll show you subsidies, accessible shifts, rights</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-xl">💼</span>
              <div>
                <p className="text-sm font-semibold text-neutral-900">What's your work style?</p>
                <p className="text-xs text-neutral-500">We'll recommend shifts that match your needs</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="flex-1 py-2.5 bg-neutral-100 text-neutral-700 rounded-xl font-semibold text-sm">
              Skip for now
            </button>
            <button onClick={() => setStep('qualifications')} className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-bold text-sm transition-all">
              Let's go
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── QUALIFICATIONS SCREEN ───────────────────────────────
  if (step === 'qualifications') {
    return (
      <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
          <h2 className="font-brand text-xl font-bold text-neutral-900 mb-1">Quick questions</h2>
          <p className="text-xs text-neutral-500 mb-5">Pick what applies to you:</p>

          {/* Migrant Worker */}
          <button
            onClick={() => {
              handleSelect('workerType', 'migrant');
              setStep('visa');
            }}
            className={`w-full p-4 rounded-xl border mb-2 text-left transition-all ${
              selections.workerType === 'migrant'
                ? 'border-brand-400 bg-brand-50'
                : 'border-neutral-100 hover:border-neutral-200'
            }`}
          >
            <div className="flex gap-3 items-start">
              <span className="text-xl">🌏</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-neutral-900">I'm a migrant worker</p>
                <p className="text-xs text-neutral-500 mt-0.5">I'm on a visa or new to Australia</p>
              </div>
              {selections.workerType === 'migrant' && <div className="text-brand-600">✓</div>}
            </div>
          </button>

          {/* Disabled Worker */}
          <button
            onClick={() => {
              handleSelect('workerType', 'disabled');
              setStep('disability');
            }}
            className={`w-full p-4 rounded-xl border mb-2 text-left transition-all ${
              selections.workerType === 'disabled'
                ? 'border-brand-400 bg-brand-50'
                : 'border-neutral-100 hover:border-neutral-200'
            }`}
          >
            <div className="flex gap-3 items-start">
              <span className="text-xl">♿</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-neutral-900">I have a disability</p>
                <p className="text-xs text-neutral-500 mt-0.5">I may need adjustments or subsidies</p>
              </div>
              {selections.workerType === 'disabled' && <div className="text-brand-600">✓</div>}
            </div>
          </button>

          {/* Standard Worker */}
          <button
            onClick={() => {
              handleSelect('workerType', 'standard');
              setStep('work-style');
            }}
            className={`w-full p-4 rounded-xl border mb-5 text-left transition-all ${
              selections.workerType === 'standard'
                ? 'border-brand-400 bg-brand-50'
                : 'border-neutral-100 hover:border-neutral-200'
            }`}
          >
            <div className="flex gap-3 items-start">
              <span className="text-xl">💼</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-neutral-900">Neither — just looking for shifts</p>
                <p className="text-xs text-neutral-500 mt-0.5">Let me explore what's available</p>
              </div>
              {selections.workerType === 'standard' && <div className="text-brand-600">✓</div>}
            </div>
          </button>

          <div className="flex gap-2">
            <button onClick={() => setStep('welcome')} className="flex-1 py-2.5 bg-neutral-100 text-neutral-700 rounded-xl font-semibold text-sm">
              Back
            </button>
            <button
              onClick={() => {
                if (selections.workerType === 'migrant') setStep('visa');
                else if (selections.workerType === 'disabled') setStep('disability');
                else setStep('work-style');
              }}
              disabled={!selections.workerType}
              className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-bold text-sm disabled:opacity-50 transition-all"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── VISA STATUS (for migrants) ───────────────────────────────
  if (step === 'visa') {
    return (
      <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
          <h2 className="font-brand text-xl font-bold text-neutral-900 mb-5">What's your visa status? 🛂</h2>

          {[
            { id: 'student', label: 'Student visa', sub: '20h/week allowed' },
            { id: 'skilled', label: 'Skilled migration visa', sub: 'Permanent or temporary' },
            { id: 'humanitarian', label: 'Humanitarian/refugee', sub: 'Unrestricted work' },
            { id: 'partner', label: 'Partner visa', sub: 'Usually unrestricted' },
            { id: 'other', label: 'Other work rights', sub: 'Not sure about my visa' },
          ].map(option => (
            <button
              key={option.id}
              onClick={() => {
                handleSelect('visaStatus', option.id);
                setStep('work-style');
              }}
              className={`w-full p-4 rounded-xl border mb-2 text-left transition-all ${
                selections.visaStatus === option.id
                  ? 'border-brand-400 bg-brand-50'
                  : 'border-neutral-100 hover:border-neutral-200'
              }`}
            >
              <p className="text-sm font-semibold text-neutral-900">{option.label}</p>
              <p className="text-xs text-neutral-500 mt-0.5">{option.sub}</p>
            </button>
          ))}

          <div className="flex gap-2 mt-6">
            <button onClick={() => setStep('qualifications')} className="flex-1 py-2.5 bg-neutral-100 text-neutral-700 rounded-xl font-semibold text-sm">
              Back
            </button>
            <button
              onClick={() => setStep('work-style')}
              disabled={!selections.visaStatus}
              className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-bold text-sm disabled:opacity-50 transition-all"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── DISABILITY TYPE ───────────────────────────────
  if (step === 'disability') {
    return (
      <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <h2 className="font-brand text-xl font-bold text-neutral-900 mb-1">Tell us a bit more</h2>
          <p className="text-xs text-neutral-500 mb-4">This helps us recommend accessible shifts & support (optional):</p>

          {[
            { id: 'physical', label: 'Physical disability', icon: '🦽' },
            { id: 'hearing', label: 'Deaf/hard of hearing', icon: '👂' },
            { id: 'vision', label: 'Blind/low vision', icon: '👁️' },
            { id: 'neurodivergent', label: 'Neurodivergent (ADHD, autism, etc)', icon: '🧠' },
            { id: 'mental', label: 'Mental health condition', icon: '💭' },
            { id: 'chronic', label: 'Chronic illness/pain', icon: '⚕️' },
            { id: 'invisible', label: 'Invisible disability', icon: '🤐' },
            { id: 'other', label: 'Something else', icon: '❓' },
            { id: 'prefer-not', label: 'Prefer not to say', icon: '🤐' },
          ].map(option => (
            <button
              key={option.id}
              onClick={() => {
                handleSelect('disabilityType', option.id);
                setStep('work-style');
              }}
              className={`w-full p-3 rounded-xl border mb-2 text-left transition-all ${
                selections.disabilityType === option.id
                  ? 'border-brand-400 bg-brand-50'
                  : 'border-neutral-100 hover:border-neutral-200'
              }`}
            >
              <div className="flex gap-2 items-center">
                <span className="text-lg">{option.icon}</span>
                <p className="text-sm font-semibold text-neutral-900">{option.label}</p>
              </div>
            </button>
          ))}

          <div className="flex gap-2 mt-6 sticky bottom-0 bg-white pt-4">
            <button onClick={() => setStep('qualifications')} className="flex-1 py-2.5 bg-neutral-100 text-neutral-700 rounded-xl font-semibold text-sm">
              Back
            </button>
            <button
              onClick={() => setStep('work-style')}
              className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-bold text-sm transition-all"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── WORK STYLE ───────────────────────────────
  if (step === 'work-style') {
    return (
      <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
          <h2 className="font-brand text-xl font-bold text-neutral-900 mb-5">What shifts suit you best?</h2>

          {[
            { id: 'flexible', label: 'Flexible hours', sub: 'Pick shifts week-to-week', icon: '⏰' },
            { id: 'recurring', label: 'Regular shifts', sub: 'Same days/times each week', icon: '📅' },
            { id: 'quick', label: 'Quick cash jobs', sub: '2-4 hour shifts', icon: '⚡' },
            { id: 'fullday', label: 'Full day shifts', sub: '8+ hours per shift', icon: '🌅' },
            { id: 'evening', label: 'Evening/weekend', sub: 'Work after hours or weekends', icon: '🌙' },
          ].map(option => (
            <button
              key={option.id}
              onClick={() => {
                handleSelect('workStyle', option.id);
                setStep('complete');
              }}
              className={`w-full p-4 rounded-xl border mb-2 text-left transition-all ${
                selections.workStyle === option.id
                  ? 'border-brand-400 bg-brand-50'
                  : 'border-neutral-100 hover:border-neutral-200'
              }`}
            >
              <div className="flex gap-3 items-start">
                <span className="text-lg">{option.icon}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-neutral-900">{option.label}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">{option.sub}</p>
                </div>
              </div>
            </button>
          ))}

          <div className="flex gap-2 mt-6">
            <button onClick={() => {
              if (selections.workerType === 'migrant') setStep('visa');
              else if (selections.workerType === 'disabled') setStep('disability');
              else setStep('qualifications');
            }} className="flex-1 py-2.5 bg-neutral-100 text-neutral-700 rounded-xl font-semibold text-sm">
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── COMPLETE SCREEN ───────────────────────────────
  if (step === 'complete') {
    const resourceMap = {
      migrant: { title: 'Migrant Worker Hub 🌏', sub: 'Banking, tax, housing, visa tracking', href: '/migrant-hub' },
      disabled: { title: 'AbilityHub ♿', sub: 'Subsidies, accessible shifts, rights', href: '/ability-hub' },
      standard: { title: 'Profile Setup', sub: 'Set your availability to get started', href: null },
    };

    const resource = resourceMap[selections.workerType];

    return (
      <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
          <div className="text-4xl mb-4 text-center">✨</div>
          <h2 className="font-brand text-2xl font-bold text-neutral-900 mb-2 text-center">You're all set!</h2>
          <p className="text-sm text-neutral-600 text-center mb-6">
            We've customized your experience. Here's what you should check out:
          </p>

          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6">
            <p className="text-xs text-brand-600 font-semibold uppercase tracking-widest mb-1">Recommended for you</p>
            <p className="text-sm font-bold text-brand-900 mb-1">{resource.title}</p>
            <p className="text-xs text-brand-700">{resource.sub}</p>
          </div>

          <div className="space-y-2 mb-6">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-widest">Also helpful:</p>
            <p className="text-xs text-neutral-600">📍 Set your availability so employers can find you</p>
            <p className="text-xs text-neutral-600">💬 Complete your profile photo (trust +20%)</p>
            <p className="text-xs text-neutral-600">📚 Check out training & certifications to earn more</p>
          </div>

          <button
            onClick={() => {
              saveQualifications();
              if (resource.href) window.location.href = resource.href;
            }}
            className="w-full py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-bold text-sm transition-all mb-2"
          >
            Check out {resource.title}
          </button>
          <button
            onClick={() => {
              saveQualifications();
              onClose();
            }}
            className="w-full py-2.5 bg-neutral-100 text-neutral-700 rounded-xl font-semibold text-sm hover:bg-neutral-200 transition-all"
          >
            Done for now
          </button>
        </div>
      </div>
    );
  }
}

export default ProfileQualificationAgent;
